import { useCallback, useEffect, useState } from 'react';
import { Layer, FeatureDetailRows } from './Layer';
import { FeatureItem } from './FeatureItem';
import { useUscgMapContext } from './UscgMapContext';
import { useListInternalLayerVersionFeaturesQuery } from 'hooks/internalLayerVersion/useListInternalLayerVersionFeaturesQuery';
import { formatDateTime } from 'components/IAP/Resources/utils/dateUtils';
import { InternalLayer } from 'hooks/internalLayer/types';
import { ExternalLayer } from 'hooks/externalLayer/types';
import { ExternalLayerFeature } from './utils/queryExternalLayerFeatures';
import { InternalFeatureList } from './InternalFeatureList';

type LayerFeaturesContentProps = {
  layer: InternalLayer | ExternalLayer;
  layerType: 'internal' | 'external';
  showAddObjectButton?: boolean;
  showDeleteObjectButton?: boolean;
  onZoomToExternalGraphic?: (graphic: __esri.Graphic) => void;
  onEdit?: (featureId: string) => Promise<void> | void;
  onDelete?: (featureId: string) => Promise<void> | void;
};

export function LayerFeaturesContent({
  layer,
  layerType,
  onZoomToExternalGraphic,
  onEdit,
  onDelete,
  showAddObjectButton,
}: LayerFeaturesContentProps) {
  const {
    isPublicExternalLayersReady,
    isProtectedExternalLayersReady,
    isExternalLayersReady,
    getExternalLayerFeatures,
    zoomToLocation,
    getSelectedVersionForLayer,
  } = useUscgMapContext();

  const [searchTerm, setSearchTerm] = useState('');

  const selectedVersion =
    layerType === 'internal' ? getSelectedVersionForLayer(layer.id) : undefined;
  const { data: internalFeatures = [], isLoading: internalLoading } =
    useListInternalLayerVersionFeaturesQuery(selectedVersion?.id);

  const [externalFeatures, setExternalFeatures] = useState<
    ExternalLayerFeature[]
  >([]);
  const [externalLoading, setExternalLoading] = useState(false);

  const fetchExternal = useCallback(async () => {
    if (layerType !== 'external') return;

    setExternalLoading(true);

    try {
      const result = await getExternalLayerFeatures(layer.id);
      setExternalFeatures(result);
    } catch (error) {
      console.error('error fetching external features for ', layer.id, error);
      setExternalFeatures([]);
    } finally {
      setExternalLoading(false);
    }
  }, [layer.id, layerType, getExternalLayerFeatures]);

  useEffect(() => {
    if (layerType !== 'external') {
      return;
    }

    const authType = layer.authType;

    if (authType === 'apiKey' && isPublicExternalLayersReady) {
      fetchExternal();
      return;
    }

    if (authType === 'userAuth' && isProtectedExternalLayersReady) {
      fetchExternal();
      return;
    }

    if (authType === null && isExternalLayersReady) {
      fetchExternal();
    }
  }, [
    fetchExternal,
    isExternalLayersReady,
    isProtectedExternalLayersReady,
    isPublicExternalLayersReady,
    layer.id,
    layer.authType,
    layerType,
  ]);

  const isLoading =
    layerType === 'internal' ? internalLoading : externalLoading;

  if (isLoading) {
    return <Layer.FeaturesList isLoading />;
  }

  const normalizedSearch = searchTerm.trim().toLowerCase();
  const filteredInternalFeatures = normalizedSearch
    ? internalFeatures.filter((f) =>
        (f.name ?? '').toLowerCase().includes(normalizedSearch)
      )
    : internalFeatures;

  return (
    <Layer.FeaturesList
      internalLayer={layerType === 'internal' ? layer : null}
      showAddObjectButton={
        showAddObjectButton &&
        selectedVersion?.approval?.approvalStatus === 'DRAFT'
      }
      searchTerm={searchTerm}
      onChangeSearchTerm={setSearchTerm}
      showSearch={layerType === 'internal'}
    >
      {layerType === 'internal' ? (
        <InternalFeatureList
          onEdit={onEdit}
          onDelete={onDelete}
          onZoomToLocation={zoomToLocation}
          features={filteredInternalFeatures.map((f) => ({
            featureId: f.id,
            key: f.id,
            title: f.name,
            resourceType: f.resourceType,
            properties: f.properties,
            updatedAt: formatDateTime(f.updatedAt),
            geometry: f.geometry,
          }))}
        />
      ) : (
        <ExternalFeatureList
          features={externalFeatures}
          onZoom={onZoomToExternalGraphic}
        />
      )}
    </Layer.FeaturesList>
  );
}

function ExternalFeatureList({
  features,
  onZoom,
}: {
  features: ExternalLayerFeature[];
  onZoom?: (graphic: __esri.Graphic) => void;
}) {
  return (
    <ul
      className="list-unstyled d-flex flex-column mb-0 gap-1"
      style={{ maxHeight: '200px', overflowY: 'auto' }}
    >
      {features.map((feature, index) => {
        const id = feature.attributes.OBJECTID ?? index;
        const geometry =
          feature.geometry?.type === 'point' ? feature.geometry : null;

        return (
          <FeatureItem
            key={id}
            externalFeature={{
              featureId: id,
              symbol: feature.symbol,
            }}
            title={
              feature.attributes.Location ?? feature.attributes.Name ?? '—'
            }
            onZoom={() => onZoom?.(feature)}
          >
            <FeatureDetailRows
              lastUpdated={formatDateTime(feature.attributes.LastEditDate)}
              point={
                geometry
                  ? { longitude: geometry.x, latitude: geometry.y }
                  : undefined
              }
            />
          </FeatureItem>
        );
      })}
    </ul>
  );
}
